// Slot generation + display formatting for PAR scheduling links.
// Ported from `selectively/src/data/schedulingRepo.js` so Margin and PAR
// produce identical slot lists for the same link config.
//
// Runs client-side in the browser's local timezone — same behavior as PAR's
// own BookingPage. The browser's TZ implicitly determines what "9–5 working
// hours" means for the prospect viewing the picker.

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function generateAvailableSlots(link, existingEvents = [], confirmedBookings = []) {
  const slots = [];
  const now = new Date();
  const minNoticeMs = (link.min_notice_hours || 4) * 60 * 60 * 1000;
  const earliest = new Date(now.getTime() + minNoticeMs);
  const latest = new Date(now.getTime() + (link.max_days_ahead || 30) * 24 * 60 * 60 * 1000);
  const durationMs = (link.duration_minutes || 90) * 60 * 1000;
  const bufferMs = (link.buffer_minutes || 0) * 60 * 1000;
  const disabledSet = new Set((link.disabled_slots || []).map((s) => new Date(s).toISOString()));

  const busy = [];
  for (const ev of existingEvents) {
    if (!ev.datetime) continue;
    const start = new Date(ev.datetime);
    const end = new Date(start.getTime() + (ev.duration_minutes || 60) * 60 * 1000);
    busy.push({ start: start.getTime() - bufferMs, end: end.getTime() + bufferMs });
  }
  for (const bk of confirmedBookings) {
    const start = new Date(bk.start_time);
    const end = new Date(bk.end_time);
    busy.push({ start: start.getTime() - bufferMs, end: end.getTime() + bufferMs });
  }

  const cursor = new Date(earliest);
  cursor.setHours(0, 0, 0, 0);

  while (cursor <= latest) {
    const dayKey = DAY_KEYS[cursor.getDay()];
    const dayConfig = link.working_hours?.[dayKey];

    if (dayConfig?.enabled) {
      const [startH, startM] = dayConfig.start.split(':').map(Number);
      const [endH, endM] = dayConfig.end.split(':').map(Number);

      const dayStart = new Date(cursor);
      dayStart.setHours(startH, startM, 0, 0);
      const dayEnd = new Date(cursor);
      dayEnd.setHours(endH, endM, 0, 0);

      const snap = 15 * 60 * 1000;
      let slotStart = new Date(Math.max(dayStart.getTime(), earliest.getTime()));
      slotStart = new Date(Math.ceil(slotStart.getTime() / snap) * snap);

      while (slotStart.getTime() + durationMs <= dayEnd.getTime()) {
        const slotEnd = new Date(slotStart.getTime() + durationMs);
        if (!disabledSet.has(slotStart.toISOString())) {
          const sMs = slotStart.getTime();
          const eMs = slotEnd.getTime();
          const conflicts = busy.some((b) => sMs < b.end && eMs > b.start);
          if (!conflicts) slots.push(new Date(slotStart));
        }
        slotStart = new Date(slotStart.getTime() + snap);
      }
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return slots;
}

export function formatSlotDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export function formatSlotTime(d) {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function groupSlotsByDate(slots) {
  const groups = new Map();
  for (const s of slots) {
    const key = formatSlotDate(s);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  return [...groups.entries()].map(([date, times]) => ({ date, times }));
}

export async function fetchPARBookingData() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const username = import.meta.env.VITE_MARGIN_PAR_USERNAME;
  const slug = import.meta.env.VITE_MARGIN_PAR_SLUG;

  if (!supabaseUrl || !anonKey || !username || !slug) {
    throw new Error('Missing PAR booking config (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY, VITE_MARGIN_PAR_USERNAME, VITE_MARGIN_PAR_SLUG)');
  }

  const res = await fetch(`${supabaseUrl}/functions/v1/create-booking`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({ action: 'get_busy', username, slug }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`PAR get_busy failed (${res.status}): ${text}`);
  }
  return res.json();
}
